import React, { forwardRef } from "react";
import { IMaskInput } from "react-imask";
import { Label } from "../Label/Label";

interface CommissionInputProps
  extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    "defaultValue" | "min" | "max"
  > {
  label: string;
  helperText?: string;
  onChangeCommission: (value: string) => void;
  defaultValue?: number;
  dataCy?: string;
  integerOnly?: boolean;
  minValue?: number;
  maxValue?: number;
  suffix?: string;
}

/** Alinha a decimal(5,2): no máximo 2 casas fracionárias + clamp min/max. */
function parseCommissionDecimal(
  value: unknown,
  minValue: number,
  maxValue: number,
): string {
  const raw = String(value ?? "").trim();
  if (raw === "") return "";

  const n = Number.parseFloat(raw.replace(",", "."));
  if (!Number.isFinite(n)) {
    return Number(minValue).toFixed(2);
  }

  const rounded = Math.round(n * 100) / 100;
  const clamped = Math.min(Math.max(rounded, minValue), maxValue);
  return clamped.toFixed(2);
}

const CommissionInput = forwardRef<HTMLInputElement, CommissionInputProps>(
  (
    {
      label,
      helperText,
      defaultValue,
      onChangeCommission,
      dataCy,
      integerOnly = false,
      minValue = 0,
      maxValue = 99,
      suffix = "%",
      ...inputProps
    },
    ref
  ) => {
    return (
      <div className="w-full flex gap-2 flex-col">
        {label && <Label>{label}</Label>}
        <div
          className={`commission-input m-0 px-3 py-2 w-full flex focus:outline-none items-center rounded-lg overflow-hidden ${
            helperText ? "border-borderError" : "border-colorBorder"
          }`}
          style={{ borderWidth: "1px", backgroundColor: "#09090B" }}
        >
          <IMaskInput
            ref={ref}
            mask={Number}
            scale={integerOnly ? 0 : 2}
            thousandsSeparator={""}
            padFractionalZeros={!integerOnly}
            normalizeZeros={true}
            radix="."
            mapToRadix={[","]}
            unmask={true}
            onAccept={(value, mask) => {
              if (integerOnly) {
                const raw = String(value ?? "");
                if (raw.trim() === "") {
                  if (mask && mask.value !== "") {
                    mask.value = "";
                  }
                  onChangeCommission("");
                  return;
                }

                const n = Number.parseInt(raw, 10);
                const clamped = Number.isFinite(n)
                  ? Math.min(Math.max(n, minValue), maxValue)
                  : minValue;

                if (mask && mask.value !== String(clamped)) {
                  mask.value = String(clamped);
                }

                onChangeCommission(String(clamped));
                return;
              }

              const parsed = parseCommissionDecimal(value, minValue, maxValue);
              if (parsed === "") {
                if (mask && mask.value !== "") {
                  mask.value = "";
                }
                onChangeCommission("");
                return;
              }

              if (mask && Number(mask.unmaskedValue) !== Number(parsed)) {
                mask.unmaskedValue = parsed;
              }

              onChangeCommission(parsed);
            }}
            defaultValue={defaultValue}
            data-cy={dataCy}
            placeholder={integerOnly ? "0" : "0.00"}
            inputMode={integerOnly ? "numeric" : "decimal"}
            className={`bg-background flex-1 text-white border-none focus:outline-none focus:ring-0`}
            min={Number(minValue) as any}
            max={Number(maxValue) as any}
          />
          <span className="text-muted-foreground ml-2">{suffix}</span>
        </div>
        {helperText && <p className="text-sm text-error mt-1">{helperText}</p>}
      </div>
    );
  }
);

export default CommissionInput;
